import { trigger, transition, useAnimation } from '@angular/animations';
import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  ElementRef,
  HostBinding,
  HostListener,
  inject,
  input,
  Renderer2
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { distinctUntilChanged, map, switchMap } from 'rxjs/operators';

import { ContextService } from 'idt-ecommerce-api/context/context.service';
import { StoreLanguage } from 'idt-ecommerce-api/stores/stores.models';
import * as Animations from 'render/render-shared/render-shared.animations';
import { RenderState } from 'render/render.state';
import { RenderByDeviceService } from 'render/services/base-render-by-device/render-by-device.service';

import { closeAllModals } from './store/render-modal-panel.actions';
import { selectIsModalOpen, selectModalProps } from './store/render-modal-panel.state';


type ModalType = 'default' | 'medium' | 'large' | 'full' | 'notification' | 'toast';

@Component({
  selector: 'render-modal-panel',
  templateUrl: 'render-modal-panel.component.html',
  styleUrls: ['./render-modal-panel.component.scss'],
  exportAs: 'render-modal-panel',
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('ModalInOut', [
      transition(':leave', [
        useAnimation(Animations.slideFade, {
          params: {
            time: '500ms',
            opacityStart: 1,
            opacityEnd: 0,
            translateStart: '0',
            translateEnd: '2rem'
          }
        })
      ]),
      transition(':enter', [
        useAnimation(Animations.fade, {
          params: {
            time: '300ms',
            start: 0,
            end: 1
          }
        })
      ])
    ])
  ]
})
export class RenderModalPanelComponent {

  @HostBinding('attr.role') public readonly roleAttr = 'dialog';

  public readonly closeOnEsc = input(true, { transform: booleanAttribute });
  public readonly closeOnOutsideClick = input(true, { transform: booleanAttribute });
  public readonly hasToShowCloseButton = input(true, { transform: booleanAttribute });
  public readonly scrollableOnSmallViewport = input(false, { transform: booleanAttribute });
  public readonly modalName = input<string>('');
  public readonly modalType = input<ModalType>('default');
  public readonly addToBody = input(false, { transform: booleanAttribute });

  @HostBinding('attr.modalType')
  public get modalTypeAttr(): ModalType {
    return this.modalType();
  }

  @HostBinding('attr.scrollable-small')
  public get scrollableSmallAttr(): '' | null {
    return this.scrollableOnSmallViewport() ? '' : null;
  }

  @HostBinding('class.open')
  public get open(): boolean {
    return this.openState();
  }

  @HostBinding('class.rtl')
  public get rtl(): boolean {
    return this.rtlState();
  }

  public readonly props$ = toObservable(this.modalName).pipe(
    switchMap((modalName: string) => this.store.select(selectModalProps(modalName)))
  );

  public readonly open$ = toObservable(this.modalName).pipe(
    switchMap((modalName: string) => this.store.select(selectIsModalOpen(modalName)))
  );

  private readonly store = inject<Store<RenderState>>(Store);
  private readonly renderByDeviceService = inject(RenderByDeviceService);
  private readonly contextService = inject(ContextService);
  private readonly elementRef = inject(ElementRef);
  private readonly renderer = inject(Renderer2);
  private readonly destroyRef = inject(DestroyRef);

  private originalParent: HTMLElement | null = null;
  private originalNextSibling: Node | null = null;

  private readonly openState = toSignal(this.open$, { initialValue: false });
  private readonly rtlState = toSignal(
    this.contextService.getCurrentLanguage$().pipe(
      map((currentLang: StoreLanguage) => currentLang?.direction === 'rtl'),
      distinctUntilChanged()
    ),
    { initialValue: false }
  );

  constructor() {
    effect(() => {
      if (!this.addToBody()) {
        return;
      }

      if (this.openState()) {
        this.moveToBody();
      } else {
        this.returnToOriginalPlace();
      }
    });

    this.destroyRef.onDestroy(() => this.returnToOriginalPlace());
  }

  @HostListener('click', ['$event.target'])
  public onClick(target: EventTarget | null): void {
    const isClickedOverlay = target instanceof HTMLElement && target.classList.contains('overlay');
    if (this.closeOnOutsideClick() && isClickedOverlay) {
      this.close();
    }
  }

  public get isMobile(): boolean {
    return this.renderByDeviceService.isMobile;
  }

  public close(): void {
    document.body.style.overflowY = '';
    this.store.dispatch(closeAllModals());
  }

  private moveToBody(): void {
    const host = this.elementRef.nativeElement as HTMLElement;
    if (host.parentElement === document.body) {
      return;
    }
    this.originalParent = host.parentElement;
    this.originalNextSibling = host.nextSibling;
    this.renderer.appendChild(document.body, host);
  }

  private returnToOriginalPlace(): void {
    const host = this.elementRef.nativeElement as HTMLElement;
    if (!this.originalParent) {
      return;
    }

    if (this.originalNextSibling?.parentNode === this.originalParent) {
      this.originalParent.insertBefore(host, this.originalNextSibling);
    } else {
      this.originalParent.appendChild(host);
    }
    this.originalParent = null;
    this.originalNextSibling = null;
  }

}
